import React, { useId, useState } from 'react';

interface FloatingLabelPillProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  trailingIcon?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  multiline?: boolean;
  rows?: number;
  focused?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  'data-testid'?: string;
}

const HEIGHT_CLASS: Record<NonNullable<FloatingLabelPillProps['size']>, string> = {
  sm: 'h-10',
  md: 'h-[46px]',
  lg: 'h-14',
  xl: 'h-[67px]',
};

/**
 * FloatingLabelPill — outlined rounded-[50px] text field with a floating label.
 * Matches Figma MUI-style outlined input: grey border by default, purple border
 * only when focused; label always rendered in text/primary with a paper-coloured
 * mask strip cutting through the border when raised.
 */
export default function FloatingLabelPill({
  label,
  value,
  onChange,
  placeholder,
  trailingIcon,
  error,
  disabled,
  readOnly,
  multiline = false,
  rows = 4,
  focused: focusedProp,
  size = 'md',
  className = '',
  'data-testid': testId,
}: FloatingLabelPillProps) {
  const id = useId();
  const [internalFocused, setInternalFocused] = useState(false);
  const focused = focusedProp ?? internalFocused;
  const hasValue = value.length > 0;
  const labelRaised = focused || hasValue;

  const borderClass = error
    ? 'border-error-500'
    : focused
      ? 'border-primary-500'
      : 'border-text-secondary';

  const labelColorClass = error
    ? 'text-error-500'
    : labelRaised
      ? 'text-text-primary'
      : 'text-text-disabled';

  const inputBaseClass =
    'w-full bg-transparent border-0 outline-none text-text-primary text-[15px] leading-6 font-sans font-normal';

  return (
    <div className={`relative w-full ${className}`}>
      {/* Floating label — with paper mask strip cutting the border when raised */}
      <div
        aria-hidden={!labelRaised}
        className={`absolute z-[2] flex items-center pointer-events-none transition-all duration-150 ease-out ${
          labelRaised ? 'top-[-9px] px-0.5' : 'top-1/2 -translate-y-1/2 p-0'
        }`}
        style={{ left: '18px' }}
      >
        {labelRaised && (
          <span
            aria-hidden="true"
            className="absolute inset-x-0 top-[9px] h-0.5 bg-surface-primary -z-10"
          />
        )}
        <label
          htmlFor={id}
          className={`font-sans whitespace-nowrap ${labelColorClass} ${
            labelRaised ? 'text-base leading-[18px] font-semibold' : 'text-[15px] leading-6 font-normal'
          }`}
        >
          {label}
        </label>
      </div>

      <div
        className={`flex ${multiline ? 'items-start p-0' : 'items-center pr-2'} gap-2 ${
          multiline ? 'rounded-2xl' : 'rounded-[50px]'
        } border bg-transparent transition-colors duration-150 ease-out ${borderClass}`}
      >
        {multiline ? (
          <textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setInternalFocused(true)}
            onBlur={() => setInternalFocused(false)}
            placeholder={focused && !hasValue ? placeholder : ''}
            disabled={disabled}
            readOnly={readOnly}
            rows={rows}
            data-testid={testId}
            className={`${inputBaseClass} resize-y py-3.5 px-[18px]`}
          />
        ) : (
          <input
            id={id}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setInternalFocused(true)}
            onBlur={() => setInternalFocused(false)}
            placeholder={focused && !hasValue ? placeholder : ''}
            disabled={disabled}
            readOnly={readOnly}
            data-testid={testId}
            className={`${inputBaseClass} px-[14px] ${HEIGHT_CLASS[size]}`}
          />
        )}
        {trailingIcon && !multiline && (
          <div className="flex-shrink-0 mr-1">{trailingIcon}</div>
        )}
      </div>

      {multiline && trailingIcon && (
        <div className="absolute top-3 right-3.5">{trailingIcon}</div>
      )}

      {error && (
        <p role="alert" className="mt-1 ml-[18px] text-xs font-sans text-error-500">
          {error}
        </p>
      )}
    </div>
  );
}
